// ============================================================
//  TASKER BREAKDOWN PAGE — ADMIN DASHBOARD
//  Path: src/pages/TaskerBreakdownPage.tsx
// ============================================================

import React, { useEffect, useState, useCallback, useRef } from 'react'
import { getTaskerBreakdown, listAllCategories } from '../services/admin.service'

// CHANGE (with the backend getTaskerBreakdown update):
//  - The breakdown now includes cities that have customers but no
//    approved experts yet (count 0). The "zero experts — growth
//    opportunities" callout below could never trigger before (the old
//    backend only returned cities that already had experts); it now
//    lists those cities with how many customers are there, busiest first.
//    With a category filter, customer figures are null (customers have
//    no category), so the callout is limited to what the filter implies.
//  - A failed load clears the old data instead of leaving the previous
//    category's numbers on screen under the new selection.
//  - Switching categories quickly can't show a superseded request's data.
//  - Callout keys include the province (two "Unknown"s or two cities with
//    the same name in different provinces used to collide).

const PROVINCE_NAMES: Record<string, string> = {
  AB: 'Alberta', BC: 'British Columbia', MB: 'Manitoba',
  NB: 'New Brunswick', NL: 'Newfoundland and Labrador',
  NS: 'Nova Scotia', NT: 'Northwest Territories', NU: 'Nunavut',
  ON: 'Ontario', PE: 'Prince Edward Island', QC: 'Quebec',
  SK: 'Saskatchewan', YT: 'Yukon',
}
const expandProvince = (p: string | null) =>
  p ? (PROVINCE_NAMES[p.toUpperCase()] ?? p) : 'Unknown Province'

// customers / onlineExperts are sent by the updated backend; optional so
// this page still works against the old one.
interface CityRow     { city: string | null; count: number; customers?: number | null; onlineExperts?: number }
interface ProvinceRow { province: string | null; total: number; cities: CityRow[]; customers?: number | null }
interface CountryRow  { country: string | null; total: number; provinces: ProvinceRow[]; customers?: number | null }
interface BreakdownResult { totalExperts: number; totalCustomers?: number | null; breakdown: CountryRow[] }
interface CategoryOption  { id: string; name: string }

const TaskerBreakdownPage: React.FC = () => {
  const [data, setData]             = useState<BreakdownResult | null>(null)
  const [loading, setLoading]       = useState(true)
  const [error, setError]           = useState('')
  const [categories, setCategories] = useState<CategoryOption[]>([])
  const [categoryFilter, setCategoryFilter] = useState('')
  const [expanded, setExpanded]     = useState<Set<string>>(new Set())
  // Latest request wins — see header.
  const requestSeq = useRef(0)

  useEffect(() => {
    listAllCategories()
      .then((res: any) => setCategories((res?.data?.data ?? []).map((c: any) => ({ id: c.id, name: c.name }))))
      .catch((err: any) => console.error('Failed to load categories for the filter:', err))
  }, [])

  const fetchBreakdown = useCallback(async () => {
    const seq = ++requestSeq.current
    setLoading(true)
    setError('')
    try {
      const res: any = await getTaskerBreakdown(categoryFilter ? { categoryId: categoryFilter } : undefined)
      if (seq !== requestSeq.current) return
      const result: BreakdownResult = res?.data?.data ?? null
      setData(result)
      if (result?.breakdown) {
        const keys = new Set<string>()
        result.breakdown.forEach((country: CountryRow) =>
          country.provinces?.forEach((p: ProvinceRow) =>
            keys.add(`${country.country ?? '__null__'}::${p.province ?? '__null__'}`)
          )
        )
        setExpanded(keys)
      }
    } catch (err: any) {
      if (seq !== requestSeq.current) return
      console.error('Failed to load tasker breakdown:', err)
      setData(null)
      setError(err?.response?.data?.message || (err?.isNetworkError
        ? 'Could not reach the server — check your connection and try again.'
        : 'Failed to load tasker breakdown'))
    }
    finally { if (seq === requestSeq.current) setLoading(false) }
  }, [categoryFilter])

  useEffect(() => { fetchBreakdown() }, [fetchBreakdown])

  const toggle = (key: string) =>
    setExpanded((prev: Set<string>) => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n })

  const allProvinces   = data?.breakdown.flatMap((c: CountryRow) => c.provinces) ?? []
  const totalCities    = allProvinces.reduce((s: number, p: ProvinceRow) => s + p.cities.length, 0)
  // Cities with customers but no approved experts — busiest first. Each
  // keeps its province so two same-named cities stay distinct.
  const zeroCities = allProvinces
    .flatMap((p: ProvinceRow) => p.cities.map((c: CityRow) => ({ ...c, province: p.province })))
    .filter((c) => c.count === 0)
    .sort((a, b) => (b.customers ?? 0) - (a.customers ?? 0))

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white">Tasker Coverage</h1>
        <p className="text-gray-400 text-sm mt-1">Geographic breakdown of approved experts — identify where to grow next</p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-6">
        <select value={categoryFilter}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setCategoryFilter(e.target.value)}
          className="bg-surface-tertiary border border-border-light rounded-xl px-4 py-2.5 text-white text-sm focus:border-brand-500 focus:outline-none min-w-[220px]">
          <option value="">All Categories</option>
          {categories.map((c: CategoryOption) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <button onClick={fetchBreakdown}
          className="px-4 py-2.5 bg-brand-600 hover:bg-brand-500 text-white rounded-xl text-sm font-medium transition-colors">
          Refresh
        </button>
      </div>

      {/* Summary cards */}
      {data && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
          {[
            { label: 'Total Experts', value: data.totalExperts,          icon: '🔧', color: 'bg-purple-500/20 text-purple-400' },
            { label: 'Countries',     value: data.breakdown.length,       icon: '🌍', color: 'bg-green-500/20 text-green-400' },
            { label: 'Provinces',     value: allProvinces.length,          icon: '📍', color: 'bg-blue-500/20 text-blue-400' },
            { label: 'Cities',        value: totalCities,                  icon: '🏙️', color: 'bg-orange-500/20 text-orange-400' },
          ].map(s => (
            <div key={s.label} className="bg-surface-card border border-border-light rounded-2xl p-4">
              <div className={`w-9 h-9 rounded-xl ${s.color} flex items-center justify-center mb-3 text-sm`}>{s.icon}</div>
              <p className="text-xl font-bold text-white">{s.value}</p>
              <p className="text-xs text-gray-400 mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>
      )}

      {error && <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-4 text-red-400 text-sm">{error}</div>}

      {loading ? (
        <div className="bg-surface-card border border-border-light rounded-2xl p-12 text-center text-gray-500">Loading...</div>
      ) : !data ? null : data.breakdown.length === 0 ? (
        <div className="bg-surface-card border border-border-light rounded-2xl p-12 text-center text-gray-500">
          No experts found{categoryFilter ? ' in this category' : ''}. Ask experts to complete their profile with a full address.
        </div>
      ) : (
        <div className="space-y-4">
          {data.breakdown.map((country: CountryRow) => (
            <div key={country.country ?? '__null__'} className="bg-surface-card border border-border-light rounded-2xl overflow-hidden">
              {/* Country header */}
              <div className="px-5 py-3.5 bg-surface-tertiary/40 flex items-center gap-3 border-b border-border-light">
                <span className="text-xl">🌍</span>
                <span className="font-semibold text-white">{country.country ?? 'Unknown Country'}</span>
                <span className="px-2.5 py-0.5 text-xs rounded-full bg-green-500/20 text-green-400 font-medium">
                  {country.total} expert{country.total !== 1 ? 's' : ''}
                </span>
                <span className="text-xs text-gray-500 ml-auto">{country.provinces.length} province{country.provinces.length !== 1 ? 's' : ''}</span>
              </div>

              {/* Provinces */}
              <div className="divide-y divide-border-light">
                {country.provinces.map((province: ProvinceRow) => {
                  const pKey = `${country.country ?? '__null__'}::${province.province ?? '__null__'}`
                  const isExpanded = expanded.has(pKey)
                  return (
                    <div key={pKey}>
                      <button onClick={() => toggle(pKey)}
                        className="w-full flex items-center gap-3 px-5 py-3.5 hover:bg-surface-tertiary/30 transition-colors text-left">
                        <span className="text-base">📍</span>
                        <div className="flex-1">
                          <span className="text-sm font-medium text-gray-200">{expandProvince(province.province)}</span>
                          {province.province && province.province !== expandProvince(province.province) && (
                            <span className="text-xs text-gray-600 ml-2">({province.province})</span>
                          )}
                        </div>
                        <span className="px-2.5 py-0.5 text-xs rounded-full bg-purple-500/20 text-purple-400 font-medium">
                          {province.total} expert{province.total !== 1 ? 's' : ''}
                        </span>
                        <span className="text-xs text-gray-500 w-16 text-right">{province.cities.length} cit{province.cities.length !== 1 ? 'ies' : 'y'}</span>
                        <span className="text-gray-500 text-xs w-4">{isExpanded ? '▲' : '▼'}</span>
                      </button>

                      {isExpanded && (
                        <div className="bg-surface-tertiary/10">
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 p-4">
                            {province.cities.map((city: CityRow) => (
                              <div key={city.city ?? '__null__'}
                                className={`flex items-center justify-between px-4 py-2.5 rounded-xl border ${city.count === 0 ? 'border-red-500/20 bg-red-500/5' : 'border-border-light bg-surface-card'}`}>
                                <div className="flex items-center gap-2 min-w-0">
                                  <span className="text-sm">{city.count === 0 ? '⚠️' : '🏙️'}</span>
                                  <span className={`text-sm truncate ${city.count === 0 ? 'text-red-400' : 'text-gray-200'}`}>
                                    {city.city ?? 'Unknown'}
                                  </span>
                                </div>
                                <span className="ml-2 flex-shrink-0 text-right">
                                  <span className={`text-sm font-bold ${city.count === 0 ? 'text-red-400' : 'text-white'}`}>
                                    {city.count}
                                  </span>
                                  {city.customers != null && (
                                    <span className="block text-xs text-blue-400" title="Customers in this city">
                                      {city.customers} customer{city.customers !== 1 ? 's' : ''}
                                    </span>
                                  )}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Zero-coverage callout */}
      {zeroCities.length > 0 && (
        <div className="mt-6 bg-orange-500/10 border border-orange-500/30 rounded-2xl p-5">
          <p className="text-sm font-semibold text-orange-400 mb-1">
            ⚠️ {zeroCities.length} cit{zeroCities.length !== 1 ? 'ies' : 'y'} with customers but no experts yet — growth opportunities
          </p>
          <p className="text-xs text-orange-300/70 mb-3">Busiest first.</p>
          <div className="flex flex-wrap gap-2">
            {zeroCities.map((c, i) => (
              <span key={`${c.province ?? '__null__'}::${c.city ?? '__null__'}::${i}`}
                className="px-3 py-1 text-xs bg-orange-500/10 text-orange-300 rounded-lg border border-orange-500/20">
                {c.city ?? 'Unknown'}{c.province ? ` (${c.province})` : ''}
                {c.customers != null && <span className="text-orange-200 font-semibold"> · {c.customers} customer{c.customers !== 1 ? 's' : ''}</span>}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default TaskerBreakdownPage